import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import { useGeneral } from '../../../contexts';
import { useEventDetails } from '../../../hooks/useEventDetails';
import Services from '../../../services/Services';
import Button from '../../../components/ui/Button/Button';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import Modal from '../../../components/ui/Modal/Modal';
import ImageUploadModal from '../../../components/ui/ImageUploadModal/ImageUploadModal';
import { Tabs, Tab } from '../../../components/ui/Tabs';
import transitionStyles from '../../../styles/transitions.module.css';
import FormInput from '../../../components/ui/Form/FormInput';
import FormTextarea from '../../../components/ui/Form/FormTextarea';
import formStyles from '../../../components/ui/Form/Form.module.css';
import { Sponsor } from '../../../types/sponsor.types';
import { Ticket } from '../../../types/ticket.types';
import { CoverFile } from '../../../types';
import { OrganizerPanelOutletContext, SponsorFormData, SponsorTicketFormData } from '../../../types/organizerPanel.types';
import { convertFileToBase64 } from '../../../utils/fileConversion';
import { extractEventIdFromAdminUrl } from '../../../utils/urlSlug';
import styles from './EventSponsorshipsPage.module.css';

const EventSponsorshipsPage: React.FC = () => {
  const { channel, event: basicEvent } = useOutletContext<OrganizerPanelOutletContext>();
  const location = useLocation();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;

  // Extract eventID from URL as fallback when basicEvent isn't available yet
  const eventID = useMemo(() => {
    if (basicEvent?.eventID) return basicEvent.eventID;
    return extractEventIdFromAdminUrl(location.pathname) || '';
  }, [basicEvent?.eventID, location.pathname]);

  // Fetch full event details
  const { event: eventDetails, loading: eventLoading, refreshEvent } = useEventDetails({
    token: token || null,
    eventID,
  });

  const [activeTab, setActiveTab] = useState<'sponsors' | 'offers'>('sponsors');
  
  // Sponsor modals
  const [showAddSponsorModal, setShowAddSponsorModal] = useState(false);
  const [showEditSponsorModal, setShowEditSponsorModal] = useState(false);
  const [showDeleteSponsorModal, setShowDeleteSponsorModal] = useState(false);
  const [showSponsorImageModal, setShowSponsorImageModal] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState<Sponsor | null>(null);
  
  // Sponsor ticket modals
  const [showAddOfferModal, setShowAddOfferModal] = useState(false);
  const [showEditOfferModal, setShowEditOfferModal] = useState(false);
  const [showDeleteOfferModal, setShowDeleteOfferModal] = useState(false);
  const [selectedSponsorTicket, setSelectedSponsorTicket] = useState<Ticket | null>(null);
  
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sponsorImage, setSponsorImage] = useState<string | null>(null);
  const [sponsorImageFile, setSponsorImageFile] = useState<CoverFile | null>(null);
  const [showUnsavedSponsorModal, setShowUnsavedSponsorModal] = useState(false);
  const [showUnsavedOfferModal, setShowUnsavedOfferModal] = useState(false);

  // Refs to store initial form data for unsaved changes detection
  const initialSponsorFormDataRef = useRef<SponsorFormData | null>(null);
  const initialSponsorImageRef = useRef<string | null>(null);
  const initialSponsorImageFileRef = useRef<CoverFile | null>(null);
  const initialSponsorTicketFormDataRef = useRef<SponsorTicketFormData | null>(null);

  // Sponsor form state
  const [sponsorFormData, setSponsorFormData] = useState<SponsorFormData>({
    companyTitle: '',
    companyAbout: '',
    sponsorshipType: '',
    sponsorshipAbout: '',
    website: '',
    email: '',
    instagram: '',
    x: '',
    linkedin: '',
  });

  // Sponsor ticket form state
  const [sponsorTicketFormData, setSponsorTicketFormData] = useState<SponsorTicketFormData>({
    title: '',
    description: '',
    price: '0',
    currency: 'TRY',
    trackInventory: false,
    inventory: '0',
  });

  const sponsors = eventDetails?.sponsors || [];
  // Filter only sponsor tickets (type === "sponsor")
  const sponsorTickets = (eventDetails?.tickets || []).filter(
    ticket => ticket.type === 'sponsor'
  );

  // Reset sponsor form when opening add modal
  useEffect(() => {
    if (showAddSponsorModal && !selectedSponsor) {
      const initialFormData: SponsorFormData = {
        companyTitle: '',
        companyAbout: '',
        sponsorshipType: '',
        sponsorshipAbout: '',
        website: '',
        email: '',
        instagram: '',
        x: '',
        linkedin: '',
      };
      setSponsorFormData(initialFormData);
      setSponsorImage(null);
      setSponsorImageFile(null);
      initialSponsorFormDataRef.current = initialFormData;
      initialSponsorImageRef.current = null;
      initialSponsorImageFileRef.current = null;
    }
  }, [showAddSponsorModal, selectedSponsor]);

  // Load sponsor data when opening edit modal
  useEffect(() => {
    if (showEditSponsorModal && selectedSponsor) {
      const initialFormData: SponsorFormData = {
        companyTitle: selectedSponsor.companyTitle || '',
        companyAbout: selectedSponsor.companyAbout || '',
        sponsorshipType: selectedSponsor.sponsorshipType || '',
        sponsorshipAbout: selectedSponsor.sponsorshipAbout || '',
        website: selectedSponsor.website || '',
        email: '',
        instagram: '',
        x: '',
        linkedin: '',
      };
      const initialImage = selectedSponsor.image || null;
      setSponsorFormData(initialFormData);
      setSponsorImage(initialImage);
      setSponsorImageFile(null);
      initialSponsorFormDataRef.current = initialFormData;
      initialSponsorImageRef.current = initialImage;
      initialSponsorImageFileRef.current = null;
    }
  }, [showEditSponsorModal, selectedSponsor]);

  // Reset sponsor ticket form when opening add modal
  useEffect(() => {
    if (showAddOfferModal && !selectedSponsorTicket) {
      const initialFormData: SponsorTicketFormData = {
        title: '',
        description: '',
        price: '0',
        currency: 'TRY', // Fixed to TRY
        trackInventory: false,
        inventory: '0',
      };
      setSponsorTicketFormData(initialFormData);
      initialSponsorTicketFormDataRef.current = initialFormData;
    }
  }, [showAddOfferModal, selectedSponsorTicket]);

  // Load sponsor ticket data when opening edit modal
  useEffect(() => {
    if (showEditOfferModal && selectedSponsorTicket) {
      const initialFormData: SponsorTicketFormData = {
        title: selectedSponsorTicket.title || '',
        description: selectedSponsorTicket.description || '',
        price: String(selectedSponsorTicket.price || 0),
        currency: 'TRY', // Fixed to TRY
        trackInventory: selectedSponsorTicket.trackInventory === true || selectedSponsorTicket.trackInventory === "1",
        inventory: String(selectedSponsorTicket.inventory || 0),
      };
      setSponsorTicketFormData(initialFormData);
      initialSponsorTicketFormDataRef.current = initialFormData;
    }
  }, [showEditOfferModal, selectedSponsorTicket]);

  // Check if there are unsaved changes for sponsor modal
  const hasUnsavedSponsorChanges = useMemo(() => {
    if (!showAddSponsorModal && !showEditSponsorModal) {
      return false;
    }
    if (!initialSponsorFormDataRef.current) {
      // For add modal, check if any field has been filled
      if (showAddSponsorModal) {
        return sponsorFormData.companyTitle.trim() !== '' ||
               sponsorFormData.sponsorshipType.trim() !== '' ||
               sponsorImageFile !== null;
      }
      return false;
    }
    
    const initial = initialSponsorFormDataRef.current;
    
    // Compare form fields
    const formChanged = (
      sponsorFormData.companyTitle.trim() !== initial.companyTitle.trim() ||
      sponsorFormData.sponsorshipType.trim() !== initial.sponsorshipType.trim()
    );
    
    // Compare image (check if new file was uploaded or image was removed)
    const imageChanged = sponsorImageFile !== null || 
      (sponsorImage !== initialSponsorImageRef.current);
    
    return formChanged || imageChanged;
  }, [showAddSponsorModal, showEditSponsorModal, sponsorFormData, sponsorImage, sponsorImageFile]);

  // Check if there are unsaved changes for sponsor ticket (offer) modal
  const hasUnsavedOfferChanges = useMemo(() => {
    if (!showAddOfferModal && !showEditOfferModal) {
      return false;
    }
    if (!initialSponsorTicketFormDataRef.current) {
      // For add modal, check if any field has been filled
      if (showAddOfferModal) {
        return sponsorTicketFormData.title.trim() !== '' ||
               sponsorTicketFormData.description.trim() !== '' ||
               sponsorTicketFormData.price !== '0' ||
               sponsorTicketFormData.trackInventory ||
               sponsorTicketFormData.inventory !== '0';
      }
      return false;
    }
    
    const initial = initialSponsorTicketFormDataRef.current;
    
    // Compare form fields
    return (
      sponsorTicketFormData.title.trim() !== initial.title.trim() ||
      sponsorTicketFormData.description.trim() !== initial.description.trim() ||
      sponsorTicketFormData.price !== initial.price ||
      sponsorTicketFormData.currency !== initial.currency ||
      sponsorTicketFormData.trackInventory !== initial.trackInventory ||
      sponsorTicketFormData.inventory !== initial.inventory
    );
  }, [showAddOfferModal, showEditOfferModal, sponsorTicketFormData]);

  const handleSponsorImageUpload = async (file: File) => {
    try {
      const coverFile = await convertFileToBase64(file);
      setSponsorImageFile(coverFile);
      const preview = URL.createObjectURL(file);
      setSponsorImage(preview);
    } catch (error) {
      toastError(getTranslation('organizerPanel.imageUploadError') || 'Resim yüklenemedi');
    }
  };

  // Sponsor handlers
  const handleAddSponsor = () => {
    setSelectedSponsor(null);
    setShowAddSponsorModal(true);
  };

  const handleEditSponsor = (sponsor: Sponsor) => {
    setSelectedSponsor(sponsor);
    setShowEditSponsorModal(true);
  };

  const handleDeleteSponsor = (sponsor: Sponsor) => {
    setSelectedSponsor(sponsor);
    setShowDeleteSponsorModal(true);
  };

  const handleSaveSponsor = async () => {
    if (!token || !channel?.channelID || !eventDetails?.eventID) return;

    if (!sponsorFormData.companyTitle.trim()) {
      toastError(getTranslation('organizerPanel.companyTitleRequired') || 'Şirket adı gereklidir');
      return;
    }
    if (!sponsorFormData.sponsorshipType.trim()) {
      toastError(getTranslation('organizerPanel.sponsorshipTypeRequired') || 'Sponsorluk tipi gereklidir');
      return;
    }

    setSaving(true);
    try {
      // Send image as CoverFile object (same as event cover) or existing image URL
      const imageToSend = sponsorImageFile 
        ? sponsorImageFile
        : (selectedSponsor?.image || null);

      if (selectedSponsor) {
        // Update sponsor
        await Services.SocialHiveUpdateSponsor(
          token,
          channel.channelID,
          eventDetails.eventID,
          selectedSponsor.sponsorID,
          sponsorFormData.companyTitle.trim(),
          sponsorFormData.sponsorshipType.trim(),
          "string", // companyAbout - mock value
          "string", // sponsorshipAbout - mock value
          imageToSend,
          sponsorFormData.website.trim()
        );
        toastSuccess(getTranslation('organizerPanel.sponsorUpdated') || 'Sponsor güncellendi');
      } else {
        // Add sponsor
        await Services.SocialHiveAddSponsor(
          token,
          channel.channelID,
          eventDetails.eventID,
          sponsorFormData.companyTitle.trim(),
          "string", // companyAbout - mock value
          sponsorFormData.sponsorshipType.trim(),
          "string", // sponsorshipAbout - mock value
          imageToSend,
          sponsorFormData.website.trim()
        );
        toastSuccess(getTranslation('organizerPanel.sponsorAdded') || 'Sponsor eklendi');
      }

      refreshEvent();
      setShowAddSponsorModal(false);
      setShowEditSponsorModal(false);
      setSelectedSponsor(null);
      setSponsorImage(null);
      setSponsorImageFile(null);
      initialSponsorFormDataRef.current = null;
      initialSponsorImageRef.current = null;
      initialSponsorImageFileRef.current = null;
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message ||
        (selectedSponsor 
          ? getTranslation('organizerPanel.sponsorUpdateError') || 'Sponsor güncellenemedi'
          : getTranslation('organizerPanel.sponsorAddError') || 'Sponsor eklenemedi');
      toastError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteSponsor = async () => {
    if (!token || !selectedSponsor?.sponsorID || !eventDetails?.eventID) return;

    setDeleting(true);
    try {
      await Services.SocialHiveRemoveSponsor(token, eventDetails.eventID, selectedSponsor.sponsorID);
      toastSuccess(getTranslation('organizerPanel.sponsorDeleted') || 'Sponsor silindi');
      refreshEvent();
      setShowDeleteSponsorModal(false);
      setSelectedSponsor(null);
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message ||
        getTranslation('organizerPanel.sponsorDeleteError') || 'Sponsor silinemedi';
      toastError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  // Sponsor ticket handlers
  const handleAddOffer = () => {
    setSelectedSponsorTicket(null);
    setShowAddOfferModal(true);
  };

  const handleEditOffer = (ticket: Ticket) => {
    setSelectedSponsorTicket(ticket);
    setShowEditOfferModal(true);
  };

  const handleDeleteOffer = (ticket: Ticket) => {
    setSelectedSponsorTicket(ticket);
    setShowDeleteOfferModal(true);
  };

  const handleCloseSponsorModal = () => {
    if (hasUnsavedSponsorChanges) {
      setShowUnsavedSponsorModal(true);
    } else {
      setShowAddSponsorModal(false);
      setShowEditSponsorModal(false);
      setSelectedSponsor(null);
      initialSponsorFormDataRef.current = null;
      initialSponsorImageRef.current = null;
      initialSponsorImageFileRef.current = null;
    }
  };

  const handleUnsavedSponsorModalClose = () => {
    setShowUnsavedSponsorModal(false);
  };

  const handleUnsavedSponsorModalDiscard = () => {
    // Reset form to initial values
    if (initialSponsorFormDataRef.current) {
      setSponsorFormData(initialSponsorFormDataRef.current);
      setSponsorImage(initialSponsorImageRef.current);
      setSponsorImageFile(initialSponsorImageFileRef.current);
    } else {
      // Reset to empty form for add modal
      setSponsorFormData({
        companyTitle: '',
        companyAbout: '',
        sponsorshipType: '',
        sponsorshipAbout: '',
        website: '',
        email: '',
        instagram: '',
        x: '',
        linkedin: '',
      });
      setSponsorImage(null);
      setSponsorImageFile(null);
    }
    
    setShowUnsavedSponsorModal(false);
    setShowAddSponsorModal(false);
    setShowEditSponsorModal(false);
    setSelectedSponsor(null);
    initialSponsorFormDataRef.current = null;
    initialSponsorImageRef.current = null;
    initialSponsorImageFileRef.current = null;
  };

  const handleUnsavedSponsorModalSave = async () => {
    setShowUnsavedSponsorModal(false);
    await handleSaveSponsor();
  };

  const handleCloseOfferModal = () => {
    if (hasUnsavedOfferChanges) {
      setShowUnsavedOfferModal(true);
    } else {
      setShowAddOfferModal(false);
      setShowEditOfferModal(false);
      setSelectedSponsorTicket(null);
      initialSponsorTicketFormDataRef.current = null;
    }
  };

  const handleUnsavedOfferModalClose = () => {
    setShowUnsavedOfferModal(false);
  };

  const handleUnsavedOfferModalDiscard = () => {
    // Reset form to initial values
    if (initialSponsorTicketFormDataRef.current) {
      setSponsorTicketFormData(initialSponsorTicketFormDataRef.current);
    } else {
      // Reset to empty form for add modal
      setSponsorTicketFormData({
        title: '',
        description: '',
        price: '0',
        currency: 'TRY',
        trackInventory: false,
        inventory: '0',
      });
    }
    
    setShowUnsavedOfferModal(false);
    setShowAddOfferModal(false);
    setShowEditOfferModal(false);
    setSelectedSponsorTicket(null);
    initialSponsorTicketFormDataRef.current = null;
  };

  const handleUnsavedOfferModalSave = async () => {
    setShowUnsavedOfferModal(false);
    await handleSaveOffer();
  };

  const handleSaveOffer = async () => {
    if (!token || !channel?.channelID || !eventDetails?.eventID) return;

    if (!sponsorTicketFormData.title.trim()) {
      toastError(getTranslation('organizerPanel.ticketTitleRequired') || 'Bilet başlığı gereklidir');
      return;
    }
    if (sponsorTicketFormData.trackInventory && (!sponsorTicketFormData.inventory || parseInt(sponsorTicketFormData.inventory) < 0)) {
      toastError(getTranslation('organizerPanel.inventoryRequired') || 'Envanter bilgisi gereklidir');
      return;
    }

    setSaving(true);
    try {
      const price = parseFloat(sponsorTicketFormData.price) || 0;
      const inventory = sponsorTicketFormData.trackInventory ? (parseInt(sponsorTicketFormData.inventory) || 0) : null;

      if (selectedSponsorTicket) {
        // Update sponsor ticket
        await Services.SocialHiveUpdateTicketToEvent(
          token,
          channel.channelID,
          eventDetails.eventID,
          selectedSponsorTicket.ticketID,
          sponsorTicketFormData.title.trim(),
          'sponsor',
          sponsorTicketFormData.description.trim(),
          price,
          sponsorTicketFormData.currency,
          sponsorTicketFormData.trackInventory,
          inventory
        );
        toastSuccess(getTranslation('organizerPanel.offerUpdated') || 'Teklif güncellendi');
      } else {
        // Add sponsor ticket
        await Services.SocialHiveAddTicketToEvent(
          token,
          channel.channelID,
          eventDetails.eventID,
          sponsorTicketFormData.title.trim(),
          'sponsor',
          sponsorTicketFormData.description.trim(),
          price,
          sponsorTicketFormData.currency,
          sponsorTicketFormData.trackInventory,
          inventory
        );
        toastSuccess(getTranslation('organizerPanel.offerAdded') || 'Teklif eklendi');
      }

      refreshEvent();
      setShowAddOfferModal(false);
      setShowEditOfferModal(false);
      setSelectedSponsorTicket(null);
      initialSponsorTicketFormDataRef.current = null;
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message ||
        (selectedSponsorTicket 
          ? getTranslation('organizerPanel.offerUpdateError') || 'Teklif güncellenemedi'
          : getTranslation('organizerPanel.offerAddError') || 'Teklif eklenemedi');
      toastError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteOffer = async () => {
    if (!token || !selectedSponsorTicket?.ticketID || !eventDetails?.eventID) return;

    setDeleting(true);
    try {
      await Services.SocialHiveRemoveTicketFromEvent(
        token,
        eventDetails.eventID,
        selectedSponsorTicket.ticketID
      );
      toastSuccess(getTranslation('organizerPanel.offerDeleted') || 'Teklif silindi');
      refreshEvent();
      setShowDeleteOfferModal(false);
      setSelectedSponsorTicket(null);
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message ||
        getTranslation('organizerPanel.offerDeleteError') || 'Teklif silinemedi';
      toastError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={styles.eventSponsorshipsPage}>
      {(eventLoading || !eventDetails) ? (
        <div className={styles.content}>
          <div className={styles.header}>
            <LoadingSkeleton height={32} width="200px" />
            <LoadingSkeleton height={40} width="120px" />
          </div>
          <div className={styles.sponsorsList}>
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={`skeleton-${index}`} className={styles.sponsorCard}>
                <div style={{ marginRight: '16px' }}>
                  <LoadingSkeleton height={67.5} width={120} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: '8px' }}>
                    <LoadingSkeleton height={20} width="60%" />
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <LoadingSkeleton height={16} width="40%" />
                  </div>
                  <LoadingSkeleton height={14} width="80%" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
      <ImageUploadModal
        isOpen={showSponsorImageModal}
        onClose={() => setShowSponsorImageModal(false)}
        onUpload={handleSponsorImageUpload}
        title={getTranslation('organizerPanel.uploadSponsorImage') || 'Sponsor Logo Yükle'}
        aspectRatio="16:9"
        recommendedSize="1920x1080"
        getTranslation={getTranslation}
      />

      {/* Sponsor Modals */}
      <Modal
        isOpen={showAddSponsorModal || showEditSponsorModal}
        onClose={handleCloseSponsorModal}
        title={selectedSponsor 
          ? (getTranslation('organizerPanel.editSponsor') || 'Sponsor Düzenle')
          : (getTranslation('organizerPanel.addSponsor') || 'Sponsor Ekle')
        }
        variant="default"
        className={styles.sponsorModal}
      >
        <div className={styles.modalContent}>
          <div className={styles.imageSection}>
            <label className={styles.label}>
              {getTranslation('organizerPanel.sponsorLogo') || 'Sponsor Logo'}
            </label>
            <div
              className={styles.imagePlaceholder}
              onClick={() => setShowSponsorImageModal(true)}
            >
              {sponsorImage ? (
                <img src={sponsorImage} alt="Sponsor" className={styles.sponsorImageModal} />
              ) : (
                <div className={styles.uploadIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          </div>

          <div className={formStyles.formFields}>
            <div className={formStyles.formField}>
              <label className={styles.label}>
                {getTranslation('organizerPanel.companyTitle') || 'Şirket Adı'} *
              </label>
              <FormInput
                type="text"
                value={sponsorFormData.companyTitle}
                onChange={(e) => setSponsorFormData({ ...sponsorFormData, companyTitle: e.target.value })}
                placeholder={getTranslation('organizerPanel.companyTitlePlaceholder') || 'Şirket adı'}
              />
            </div>

            <div className={formStyles.formField}>
              <label className={styles.label}>
                {getTranslation('organizerPanel.sponsorshipType') || 'Sponsorluk Tipi'} *
              </label>
              <FormInput
                type="text"
                value={sponsorFormData.sponsorshipType}
                onChange={(e) => setSponsorFormData({ ...sponsorFormData, sponsorshipType: e.target.value })}
                placeholder={getTranslation('organizerPanel.sponsorshipTypePlaceholder') || 'Örn: Ana Sponsor, Platin Sponsor'}
              />
            </div>

            <div className={formStyles.formField}>
              <label className={styles.label}>
                {getTranslation('organizerPanel.sponsorLink') || 'Link'}
              </label>
              <p className={styles.labelDescription}>
                {getTranslation('organizerPanel.sponsorLinkDescription') || 'Users will be redirected to this link when they click on the sponsor card.'}
              </p>
              <FormInput
                type="text"
                value={sponsorFormData.website}
                onChange={(e) => setSponsorFormData({ ...sponsorFormData, website: e.target.value })}
                placeholder={getTranslation('organizerPanel.websitePlaceholder') || 'https://...'}
              />
            </div>
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              size="medium"
              onClick={handleCloseSponsorModal}
              disabled={saving}
            >
              {getTranslation('common.cancel') || 'İptal'}
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleSaveSponsor}
              disabled={saving}
            >
              {saving
                ? (getTranslation('common.saving') || 'Kaydediliyor...')
                : (getTranslation('common.save') || 'Kaydet')
              }
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showUnsavedSponsorModal}
        onClose={handleUnsavedSponsorModalClose}
        title={getTranslation('organizerPanel.unsavedChanges') || 'Kaydedilmemiş Değişiklikler'}
        variant="confirm"
        message={getTranslation('organizerPanel.unsavedChangesMessage') || 'Kaydedilmemiş değişiklikleriniz var. Devam etmek istediğinizden emin misiniz?'}
        confirmText={getTranslation('organizerPanel.saveAndContinue') || 'Kaydet ve Devam Et'}
        cancelText={getTranslation('organizerPanel.discardChanges') || 'Vazgeç'}
        onConfirm={handleUnsavedSponsorModalSave}
        onCancel={handleUnsavedSponsorModalDiscard}
        confirmVariant="primary"
      />

      <Modal
        isOpen={showUnsavedOfferModal}
        onClose={handleUnsavedOfferModalClose}
        title={getTranslation('organizerPanel.unsavedChanges') || 'Kaydedilmemiş Değişiklikler'}
        variant="confirm"
        message={getTranslation('organizerPanel.unsavedChangesMessage') || 'Kaydedilmemiş değişiklikleriniz var. Devam etmek istediğinizden emin misiniz?'}
        confirmText={getTranslation('organizerPanel.saveAndContinue') || 'Kaydet ve Devam Et'}
        cancelText={getTranslation('organizerPanel.discardChanges') || 'Vazgeç'}
        onConfirm={handleUnsavedOfferModalSave}
        onCancel={handleUnsavedOfferModalDiscard}
        confirmVariant="primary"
      />

      <Modal
        isOpen={showDeleteSponsorModal}
        onClose={() => {
          setShowDeleteSponsorModal(false);
          setSelectedSponsor(null);
        }}
        title={getTranslation('organizerPanel.deleteSponsor') || 'Sponsor Sil'}
        variant="confirm"
        message={getTranslation('organizerPanel.deleteSponsorConfirm') || 'Bu sponsoru silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.'}
        confirmText={getTranslation('common.delete') || 'Sil'}
        cancelText={getTranslation('common.cancel') || 'İptal'}
        onConfirm={handleConfirmDeleteSponsor}
        confirmVariant="primary"
        isDanger={true}
      />

      {/* Sponsor Ticket (Offer) Modals */}
      <Modal
        isOpen={showAddOfferModal || showEditOfferModal}
        onClose={handleCloseOfferModal}
        title={selectedSponsorTicket 
          ? (getTranslation('organizerPanel.editOffer') || 'Teklif Düzenle')
          : (getTranslation('organizerPanel.addOffer') || 'Teklif Ekle')
        }
        variant="default"
        className={styles.offerModal}
      >
        <div className={styles.modalContent}>
          <div className={formStyles.formFields}>
            <div className={formStyles.formField}>
              <label className={styles.label}>
                {getTranslation('organizerPanel.offerTitle') || 'Teklif Başlığı'} *
              </label>
              <FormInput
                type="text"
                value={sponsorTicketFormData.title}
                onChange={(e) => setSponsorTicketFormData({ ...sponsorTicketFormData, title: e.target.value })}
                placeholder={getTranslation('organizerPanel.offerTitlePlaceholder') || 'Teklif başlığı'}
              />
            </div>

            <div className={formStyles.formField}>
              <label className={styles.label}>
                {getTranslation('organizerPanel.offerDescription') || 'Açıklama'}
              </label>
              <FormTextarea
                value={sponsorTicketFormData.description}
                onChange={(e) => setSponsorTicketFormData({ ...sponsorTicketFormData, description: e.target.value })}
                rows={4}
                placeholder={getTranslation('organizerPanel.offerDescriptionPlaceholder') || 'Teklif açıklaması'}
              />
            </div>

            <div className={formStyles.formRow}>
              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.price') || 'Fiyat'} *
                </label>
                <FormInput
                  type="number"
                  step="0.01"
                  min="0"
                  value={sponsorTicketFormData.price}
                  onChange={(e) => setSponsorTicketFormData({ ...sponsorTicketFormData, price: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.currency') || 'Para Birimi'} *
                </label>
                <select
                  value={sponsorTicketFormData.currency}
                  onChange={(e) => setSponsorTicketFormData({ ...sponsorTicketFormData, currency: e.target.value })}
                  className={formStyles.select}
                  disabled
                >
                  <option value="TRY">TRY</option>
                </select>
              </div>
            </div>

            <div className={formStyles.formField}>
              <label className={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={sponsorTicketFormData.trackInventory}
                  onChange={(e) => setSponsorTicketFormData({ 
                    ...sponsorTicketFormData, 
                    trackInventory: e.target.checked, 
                    inventory: e.target.checked ? sponsorTicketFormData.inventory : '0' 
                  })}
                  className={styles.checkbox}
                />
                <span>{getTranslation('organizerPanel.trackInventory') || 'Envanter Takibi'}</span>
              </label>
            </div>

            {sponsorTicketFormData.trackInventory && (
              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.inventory') || 'Envanter'} *
                </label>
                <FormInput
                  type="number"
                  min="0"
                  step="1"
                  value={sponsorTicketFormData.inventory}
                  onChange={(e) => setSponsorTicketFormData({ ...sponsorTicketFormData, inventory: e.target.value.replace(/\D/g, '') })}
                  placeholder="0"
                />
              </div>
            )}
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              size="medium"
              onClick={handleCloseOfferModal}
              disabled={saving}
            >
              {getTranslation('common.cancel') || 'İptal'}
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleSaveOffer}
              disabled={saving}
            >
              {saving
                ? (getTranslation('common.saving') || 'Kaydediliyor...')
                : (getTranslation('common.save') || 'Kaydet')
              }
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteOfferModal}
        onClose={() => {
          setShowDeleteOfferModal(false);
          setSelectedSponsorTicket(null);
        }}
        title={getTranslation('organizerPanel.deleteOffer') || 'Teklif Sil'}
        variant="confirm"
        message={getTranslation('organizerPanel.deleteOfferConfirm') || 'Bu teklifi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.'}
        confirmText={getTranslation('common.delete') || 'Sil'}
        cancelText={getTranslation('common.cancel') || 'İptal'}
        onConfirm={handleConfirmDeleteOffer}
        confirmVariant="primary"
        isDanger={true}
      />

      <div className={styles.header}>
        <Tabs variant="default">
          <Tab
            variant="default"
            isActive={activeTab === 'sponsors'}
            onClick={() => setActiveTab('sponsors')}
          >
            {getTranslation('organizerPanel.sponsors') || 'Sponsorlar'}
          </Tab>
          <Tab
            variant="default"
            isActive={activeTab === 'offers'}
            onClick={() => setActiveTab('offers')}
          >
            {getTranslation('organizerPanel.offers') || 'Teklifler'}
          </Tab>
        </Tabs>
        <Button
          variant="primary"
          size="medium"
          onClick={activeTab === 'sponsors' ? handleAddSponsor : handleAddOffer}
          className={styles.addButton}
        >
          {activeTab === 'sponsors'
            ? (getTranslation('organizerPanel.addSponsor') || 'Sponsor Ekle')
            : (getTranslation('organizerPanel.addOffer') || 'Teklif Ekle')
          }
        </Button>
      </div>

      <div className={styles.content} key={activeTab}>
        <div className={transitionStyles.fadeIn}>
        {activeTab === 'sponsors' ? (
          <>
            {sponsors.length === 0 ? (
              <div className={styles.emptyState}>
                <p className={styles.emptyMessage}>
                  {getTranslation('organizerPanel.noSponsors') || 'Henüz sponsor eklenmemiş'}
                </p>
              </div>
            ) : (
              <div className={styles.sponsorsGrid}>
                {sponsors.map((sponsor) => {
                  return (
                    <div key={sponsor.sponsorID} className={styles.sponsorCard}>
                      <div className={styles.sponsorActions}>
                        <button
                          onClick={() => handleEditSponsor(sponsor)}
                          className={styles.actionIconButton}
                          aria-label={getTranslation('common.edit') || 'Düzenle'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteSponsor(sponsor)}
                          className={styles.deleteIconButton}
                          aria-label={getTranslation('common.delete') || 'Sil'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                      <div className={styles.sponsorImage}>
                        {sponsor.image ? (
                          <img src={sponsor.image} alt={sponsor.companyTitle} />
                        ) : (
                          <div className={styles.sponsorInitials}>
                            {sponsor.companyTitle?.[0]?.toUpperCase() || ''}
                            {sponsor.companyTitle?.[1]?.toUpperCase() || ''}
                          </div>
                        )}
                      </div>
                      <div className={styles.sponsorInfo}>
                        <h3 className={styles.sponsorName}>{sponsor.companyTitle}</h3>
                        {sponsor.sponsorshipType && (
                          <p className={styles.sponsorType}>{sponsor.sponsorshipType}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            {sponsorTickets.length === 0 ? (
              <div className={styles.emptyState}>
                <p className={styles.emptyMessage}>
                  {getTranslation('organizerPanel.noOffers') || 'Henüz teklif eklenmemiş'}
                </p>
              </div>
            ) : (
              <div className={styles.offersList}>
                {sponsorTickets.map((ticket) => {
                  const price = ticket.price || 0;
                  const currency = ticket.currency || 'TRY';
                  const isFree = price === 0;
                  const hasInventory = ticket.trackInventory === true || ticket.trackInventory === "1";
                  const inventory = ticket.inventory || 0;
                  const soldInventory = ticket.soldInventory || 0;
                  const remainingInventory = inventory - soldInventory;

                  return (
                    <div key={ticket.ticketID} className={styles.offerCard}>
                      <div className={styles.offerActions}>
                        <button
                          onClick={() => handleEditOffer(ticket)}
                          className={styles.actionIconButton}
                          aria-label={getTranslation('common.edit') || 'Düzenle'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteOffer(ticket)}
                          className={styles.deleteIconButton}
                          aria-label={getTranslation('common.delete') || 'Sil'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                      <div className={styles.offerInfo}>
                        <h3 className={styles.offerTitle}>{ticket.title}</h3>
                        {ticket.description && (
                          <p className={styles.offerDescription}>{ticket.description}</p>
                        )}
                        <div className={styles.offerDetails}>
                          <div className={styles.offerDetail}>
                            <span className={styles.offerDetailLabel}>
                              {getTranslation('organizerPanel.price') || 'Fiyat'}:
                            </span>
                            <span className={styles.offerDetailValue}>
                              {isFree 
                                ? (getTranslation('organizerPanel.free') || 'Ücretsiz')
                                : `${price} ${currency}`
                              }
                            </span>
                          </div>
                          {hasInventory && (
                            <div className={styles.offerDetail}>
                              <span className={styles.offerDetailLabel}>
                                {getTranslation('organizerPanel.inventory') || 'Envanter'}:
                              </span>
                              <span className={styles.offerDetailValue}>
                                {remainingInventory} / {inventory}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles.offerActionsDesktop}>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => handleEditOffer(ticket)}
                          className={styles.actionButton}
                        >
                          {getTranslation('common.edit') || 'Düzenle'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => handleDeleteOffer(ticket)}
                          className={styles.deleteButton}
                        >
                          {getTranslation('common.delete') || 'Sil'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default EventSponsorshipsPage;
